import { useMutation } from "react-query";
import { client } from "./axios";
import { config } from "../config";
import { ApiBasePathParams, ApiError } from "../types";
import { AxiosError, AxiosResponse } from "axios";

export const useFetchHostedPaymentsPageMutation = () => {
  const mutation = useMutation<
    string,
    AxiosError<ApiError>,
    ApiBasePathParams & {
      invoiceNumber: string;
      amount: number;
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      state?: string;
      zip?: string;
      paymentMethod?: string;
      saveCard?: boolean;
      authTransaction?: boolean;
    },
    unknown
  >(
    async (
      data: ApiBasePathParams & {
        invoiceNumber: string;
        amount: number;
        addressLine1?: string;
        addressLine2?: string;
        city?: string;
        state?: string;
        zip?: string;
        paymentMethod?: string;
        saveCard?: boolean;
        authTransaction?: boolean;
      },
    ) => {
      const { orgId, accountId, invoiceNumber, ...payload } = data;

      const path =
        config.api.worldpayIntegrationService.initiateWorldpayTransaction
          .replace("{orgId}", orgId.toString())
          .replace("{accountId}", accountId.toString())
          .replace("{invoiceNumber}", invoiceNumber.toString());

      const response = await client.post(path, { ...payload });

      return response.data.hostedPaymentsPageUrl;
    },
  );

  return mutation;
};

export const useFetchCreditCardSaleMutation = () => {
  const mutation = useMutation<
    string,
    AxiosError<ApiError>,
    ApiBasePathParams & {
      invoiceNumber: string;
      omnitokenId?: string | number;
      tokenId?: string;
      netTransactionId?: string;
      expirationMonth?: string;
      expirationYear?: string;
      cardLogo?: string;
      amount: number;
    },
    unknown
  >(
    async (
      data: ApiBasePathParams & {
        invoiceNumber: string;
        omnitokenId?: string | number;
        tokenId?: string;
        netTransactionId?: string;
        expirationMonth?: string;
        expirationYear?: string;
        cardLogo?: string;
        amount: number;
      },
    ) => {
      const { orgId, accountId, invoiceNumber, ...payload } = data;

      const path = config.api.worldpayIntegrationService.creditCardSale
        .replace("{orgId}", orgId.toString())
        .replace("{accountId}", accountId.toString())
        .replace("{invoiceNumber}", invoiceNumber.toString());

      const response = await client.post(path, { ...payload });

      return response.data.callback;
    },
  );

  return mutation;
};

export const useFetchAchSaleMutation = () => {
  const mutation = useMutation<
    string,
    AxiosError<ApiError>,
    ApiBasePathParams & {
      invoiceNumber: string;
      omnitokenId: string | number;
      tokenId: string;
      netTransactionId?: string;
      amount: number;
    },
    unknown
  >(
    async (
      data: ApiBasePathParams & {
        invoiceNumber: string;
        omnitokenId: string | number;
        tokenId?: string;
        netTransactionId?: string;
        amount: number;
      },
    ) => {
      const { orgId, accountId, invoiceNumber, ...payload } = data;

      const path = config.api.worldpayIntegrationService.achSale
        .replace("{orgId}", orgId.toString())
        .replace("{accountId}", accountId.toString())
        .replace("{invoiceNumber}", invoiceNumber.toString());

      const response = await client.post(path, { ...payload });

      return response.data.callback;
    },
  );

  return mutation;
};

export const useCashPaymentMutation = () => {
  const mutation = useMutation<
    string,
    AxiosError<ApiError>,
    ApiBasePathParams & {
      invoiceNumber: string;
      amount: number;
    },
    unknown
  >(
    async (
      data: ApiBasePathParams & {
        invoiceNumber: string;
        amount: number;
      },
    ) => {
      const { orgId, accountId, invoiceNumber, ...payload } = data;

      const path = config.api.worldpayIntegrationService.cashPayment
        .replace("{orgId}", orgId.toString())
        .replace("{accountId}", accountId.toString())
        .replace("{invoiceNumber}", invoiceNumber.toString());

      const response = await client.post(path, { ...payload });

      return response.data.callback;
    },
  );

  return mutation;
};

export const useCheckPaymentMutation = () => {
  const mutation = useMutation<
    string,
    AxiosError<ApiError>,
    ApiBasePathParams & {
      invoiceNumber: string;
      amount: number;
      bankName: string;
      checkNumber: string;
    },
    unknown
  >(
    async (
      data: ApiBasePathParams & {
        invoiceNumber: string;
        amount: number;
        bankName: string;
        checkNumber: string;
      },
    ) => {
      const { orgId, accountId, invoiceNumber, ...payload } = data;

      const path = config.api.worldpayIntegrationService.checkPayment
        .replace("{orgId}", orgId.toString())
        .replace("{accountId}", accountId.toString())
        .replace("{invoiceNumber}", invoiceNumber.toString());

      const response = await client.post(path, { ...payload });

      return response.data.callback;
    },
  );

  return mutation;
};

export const useMultipleCheckPaymentMutation = () => {
  const mutation = useMutation<
    string,
    AxiosError<ApiError>,
    Omit<ApiBasePathParams, "accountId"> & {
      items: {
        accountId: number;
        invoiceNumber: string;
        checkNumber: string;
        bankName: string;
        amount: number;
      }[];
    },
    unknown
  >(
    async (
      data: Omit<ApiBasePathParams, "accountId"> & {
        items: {
          accountId: number;
          invoiceNumber: string;
          checkNumber: string;
          bankName: string;
          amount: number;
        }[];
      },
    ) => {
      const { orgId, ...payload } = data;

      const path =
        config.api.worldpayIntegrationService.multipleCheckPayment.replace(
          "{orgId}",
          orgId.toString(),
        );

      const response = await client.post(path, { ...payload });

      return response.data.callback;
    },
  );

  return mutation;
};

export const useCreditCardReturnMutation = () => {
  const mutation = useMutation<
    AxiosResponse,
    AxiosError<ApiError>,
    {
      orgId: number;
      accountId: number;
      invoiceNumber: string;
      paymentRecordId: number;
      amount: number;
    }
  >(
    async (data: {
      orgId: number;
      accountId: number;
      invoiceNumber: string;
      paymentRecordId: number;
      amount: number;
    }) => {
      const { orgId, accountId, invoiceNumber, paymentRecordId, amount } = data;
      const path = config.api.worldpayIntegrationService.creditCardReturn
        .replace("{orgId}", orgId.toString())
        .replace("{accountId}", accountId.toString())
        .replace("{invoiceNumber}", invoiceNumber.toString());

      const response = await client.post(path, { paymentRecordId, amount });

      return response;
    },
  );

  return mutation;
};

export const useCreditCardReversalMutation = () => {
  const mutation = useMutation<
    AxiosResponse,
    AxiosError<ApiError>,
    {
      orgId: number;
      accountId: number;
      invoiceNumber: string;
      paymentRecordId: number;
    }
  >(
    async (data: {
      orgId: number;
      accountId: number;
      invoiceNumber: string;
      paymentRecordId: number;
    }) => {
      const { orgId, accountId, invoiceNumber, paymentRecordId } = data;
      const path = config.api.worldpayIntegrationService.creditCardReversal
        .replace("{orgId}", orgId.toString())
        .replace("{accountId}", accountId.toString())
        .replace("{invoiceNumber}", invoiceNumber.toString());

      const response = await client.post(path, { paymentRecordId });

      return response;
    },
  );

  return mutation;
};
